import { View, Text, TouchableOpacity, Image, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { spacing } from "../../theme";
function StatusPill({
  variant,
  children,
}: {
  variant: "active" | "pending" | "disabled";
  children: React.ReactNode;
}) {
  const colors = useColors();
  const styles = createStatusPillStyles(colors);
  
  return (
    <View style={[styles.pill, styles[variant]]}>
      <Text style={[styles.text, styles[`${variant}Text`]]}>{children}</Text>
    </View>
  );
}

function PrimaryButton({
  title,
  onPress,
  disabled,
  tone = "primary",
}: {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  tone?: "primary" | "success" | "warning";
}) {
  const colors = useColors();
  const styles = createButtonStyles(colors);
  
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={!!disabled}
      style={[styles.primaryButton, styles[tone], disabled && styles.disabled]}
    >
      <Text style={styles.primaryButtonText}>{title}</Text>
    </TouchableOpacity>
  );
}

function SecondaryButton({
  title,
  onPress,
}: {
  title: string;
  onPress: () => void;
}) {
  const colors = useColors();
  const styles = createButtonStyles(colors);
  
  return (
    <TouchableOpacity onPress={onPress} style={styles.secondaryButton}>
      <Text style={styles.secondaryButtonText}>{title}</Text>
    </TouchableOpacity>
  );
}

function PaymentConnectCard({
  connected,
  pending,
  onConnect,
  onComplete,
  onManage,
  onRefresh,
  isChecking,
  disabled,
  disabledReason,
}: {
  connected: boolean;
  pending: boolean;
  onConnect: () => void;
  onComplete: () => void;
  onManage: () => void;
  onRefresh: () => void;
  isChecking: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const colors = useColors();
  const navigation = useNavigation();
  const styles = createCardStyles(colors);

  const handlePaymentInfo = () => {
    navigation.navigate("PaymentProcessingInfo" as never);
  };
  
  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.header}>
        <Image
          source={require("../../assets/stripe-logo.png")}
          resizeMode="contain"
          style={{ width: 72, height: 24, marginRight: 8 }}
        />
        {connected ? (
          <StatusPill variant="active">Connected</StatusPill>
        ) : pending ? (
          <StatusPill variant="pending">Pending setup</StatusPill>
        ) : (
          <StatusPill variant="disabled">Not connected</StatusPill>
        )}
      </View>

      {/* Copy */}
      <Text style={styles.description}>
        Connect your Stripe Express account to enable payments and payouts for
        your storefront.
      </Text>

      {/* Disabled Warning */}
      {disabled && disabledReason && (
        <View style={styles.disabledWarning}>
          <Feather name="alert-circle" size={16} color={colors.warning} />
          <Text style={styles.disabledWarningText}>{disabledReason}</Text>
        </View>
      )}

      {/* Payment Information Link */}
      <TouchableOpacity style={styles.infoLink} onPress={handlePaymentInfo}>
        <Feather name="info" size={14} color={colors.primary} />
        <Text style={styles.infoLinkText}>
          Learn about payment processing & hold policies
        </Text>
        <Feather name="chevron-right" size={14} color={colors.primary} />
      </TouchableOpacity>

      {/* Actions */}
      {connected ? (
        <View style={styles.buttonRow}>
          <PrimaryButton
            title="Manage on Stripe"
            onPress={onManage}
            tone="success"
          />
          <SecondaryButton
            title={isChecking ? "Refreshing…" : "Refresh"}
            onPress={onRefresh}
          />
        </View>
      ) : pending ? (
        <View style={styles.buttonRow}>
          <PrimaryButton
            title="Complete Onboarding"
            onPress={onComplete}
            tone="warning"
            disabled={disabled}
          />
          <SecondaryButton
            title={isChecking ? "Checking…" : "Refresh status"}
            onPress={onRefresh}
          />
        </View>
      ) : (
        <View style={styles.buttonRow}>
          <PrimaryButton 
            title="Connect Stripe" 
            onPress={onConnect} 
            disabled={disabled}
          />
          <SecondaryButton
            title={isChecking ? "Checking…" : "Refresh status"}
            onPress={onRefresh}
          />
        </View>
      )}
    </View>
  );
}

const createStatusPillStyles = (colors: ThemeColors) => StyleSheet.create({
  pill: {
    paddingVertical: spacing.xs,
    paddingHorizontal: 10,
    borderRadius: 999,
  },
  text: {
    fontWeight: "600",
  },
  active: {
    backgroundColor: colors.background, // Light green background
  },
  activeText: {
    color: colors.success,
  },
  pending: {
    backgroundColor: colors.backgroundSecondary, // Light orange background
  },
  pendingText: {
    color: colors.warning,
  },
  disabled: {
    backgroundColor: colors.backgroundSecondary, // Light red background
  },
  disabledText: {
    color: colors.error,
  },
});

const createButtonStyles = (colors: ThemeColors) => StyleSheet.create({
  primaryButton: {
    flex: 1,
    paddingVertical: spacing.sm + 4,
    borderRadius: spacing.sm + 4,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonText: {
    color: colors.surface,
    fontWeight: "700",
  },
  secondaryButton: {
    flex: 1,
    paddingVertical: spacing.sm + 4,
    borderRadius: spacing.sm + 4,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.surface,
  },
  secondaryButtonText: {
    color: colors.text,
    fontWeight: "600",
  },
  primary: {
    backgroundColor: colors.primary,
  },
  success: {
    backgroundColor: colors.success,
  },
  warning: {
    backgroundColor: colors.warning,
  },
  disabled: {
    backgroundColor: colors.backgroundSecondary,
  },
});

const createCardStyles = (colors: ThemeColors) => StyleSheet.create({
  card: {
    marginHorizontal: spacing.md,
    borderRadius: spacing.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
    shadowColor: colors.shadow,
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
    borderWidth: 1,
    borderColor: colors.border,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: spacing.sm + 4,
  },
  description: {
    color: colors.textLight,
    lineHeight: 20,
    marginBottom: spacing.sm,
  },
  disabledWarning: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.backgroundSecondary,
    padding: spacing.sm,
    borderRadius: spacing.sm,
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  disabledWarningText: {
    color: colors.warning,
    fontSize: 13,
    fontWeight: "500",
    flex: 1,
  },
  infoLink: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: spacing.xs,
    marginBottom: spacing.sm,
  },
  infoLinkText: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: "500",
    marginLeft: spacing.xs,
    marginRight: spacing.xs,
    flex: 1,
  },
  buttonRow: {
    flexDirection: "row",
    gap: spacing.sm + 4,
  },
});

export default PaymentConnectCard;
